/**
 * FREESTANDING TUB (2026-10-06, Matt: "I need a free-standing tub family in the plumbing
 * fitout please"): a soaking tub that stands in the room, not in an alcove. Local frame
 * (the object convention): x across the width — the tub's LENGTH — centred on 0, y from
 * the anchor (y = 0) to d, z up from the floor; the tub is centred at (0, d/2).
 *
 * The shell is a soft oval (a superellipse — straighter sides than an ellipse, rounder
 * than a rounded rectangle, the read of a modern acrylic/stone-resin tub) LOFTED from a
 * smaller base up to a flared rim, a thin rim around the top, and a sloped interior down
 * to a basin floor. The drain sits at the −x end; a floor-mounted tub filler (on unless
 * the instance turns it off) stands just beyond that end, its spout arching over the rim.
 * Services come up through the FLOOR — a freestanding tub has no wall behind it.
 */
import type { MeshBuilder } from '../../extrude'
import type { Pt2 } from '@aj/bim-kernel'

/** superellipse exponent — 2 is an ellipse, higher squares it off */
const EXP = 3
const SEGS = 48
/** the base's size as a fraction of the rim's (the flare) */
const BASE_SCALE = 0.86
const RIM_T = 0.1
const BASIN_FLOOR_FT = 0.33
/** the filler's distance past the drain end, and its height */
export const TUB_FILLER_GAP_FT = 0.55
const FILLER_H_FT = 3.4

/** the oval outline at half-sizes (a, b), centred at (0, cy) — CCW */
function oval(a: number, b: number, cy: number): Pt2[] {
  const pts: Pt2[] = []
  for (let i = 0; i < SEGS; i++) {
    const t = (Math.PI * 2 * i) / SEGS
    const c = Math.cos(t), s = Math.sin(t)
    pts.push([a * Math.sign(c) * Math.abs(c) ** (2 / EXP), cy + b * Math.sign(s) * Math.abs(s) ** (2 / EXP)])
  }
  return pts
}

/** where the filler stands (local), or null when the tub has none */
export function tubFillerPoint(w: number, d: number): Pt2 {
  return [-w / 2 - TUB_FILLER_GAP_FT, d / 2]
}

export function buildFreestandingTub(w: number, d: number, h: number, b: MeshBuilder, filler: boolean): void {
  const A = w / 2, B = d / 2, cy = d / 2
  const tri = (p: number[], q: number[], r: number[]) => {
    const ux = q[0] - p[0], uy = q[1] - p[1], uz = q[2] - p[2]
    const vx = r[0] - p[0], vy = r[1] - p[1], vz = r[2] - p[2]
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const L = Math.hypot(nx, ny, nz) || 1
    b.addTri(p, q, r, [nx / L, ny / L, nz / L])
  }
  /** a band between two stacked rings; `out` faces away from the tub's axis */
  const band = (lo: Pt2[], zLo: number, hi: Pt2[], zHi: number, out: boolean) => {
    for (let i = 0; i < SEGS; i++) {
      const j = (i + 1) % SEGS
      const a0 = [lo[i][0], lo[i][1], zLo], a1 = [lo[j][0], lo[j][1], zLo]
      const b0 = [hi[i][0], hi[i][1], zHi], b1 = [hi[j][0], hi[j][1], zHi]
      if (out) { tri(a0, a1, b1); tri(a0, b1, b0) } else { tri(a0, b1, a1); tri(a0, b0, b1) }
    }
  }
  /** a flat fan over a ring (up = facing up) */
  const cap = (ring: Pt2[], z: number, up: boolean) => {
    const c = [0, cy, z]
    for (let i = 0; i < SEGS; i++) {
      const j = (i + 1) % SEGS
      const p = [ring[i][0], ring[i][1], z], q = [ring[j][0], ring[j][1], z]
      if (up) tri(c, p, q); else tri(c, q, p)
    }
  }
  // the outer shell, flaring from the base to the rim
  const zs = [0, h * 0.35, h * 0.75, h]
  const sc = [BASE_SCALE, 0.93, 0.98, 1]
  const outer = zs.map((_, k) => oval(A * sc[k], B * sc[k], cy))
  cap(outer[0], 0, false)
  for (let k = 0; k + 1 < zs.length; k++) band(outer[k], zs[k], outer[k + 1], zs[k + 1], true)
  // the rim, and the sloped interior down to the basin floor
  const rimIn = oval(A - RIM_T, B - RIM_T, cy)
  for (let i = 0; i < SEGS; i++) {
    const j = (i + 1) % SEGS
    const p0 = [outer[3][i][0], outer[3][i][1], h], p1 = [outer[3][j][0], outer[3][j][1], h]
    const q0 = [rimIn[i][0], rimIn[i][1], h], q1 = [rimIn[j][0], rimIn[j][1], h]
    tri(p0, p1, q1); tri(p0, q1, q0)
  }
  const mid = oval((A - RIM_T) * 0.94, (B - RIM_T) * 0.9, cy)
  const floor = oval((A - RIM_T) * 0.78, (B - RIM_T) * 0.62, cy)
  const zMid = BASIN_FLOOR_FT + (h - BASIN_FLOOR_FT) * 0.45
  band(mid, zMid, rimIn, h, false)
  band(floor, BASIN_FLOOR_FT, mid, zMid, false)
  cap(floor, BASIN_FLOOR_FT, true)

  if (!filler) return
  // the floor-mounted filler: a post, its lever, and a spout arching over the rim
  const [fx, fy] = tubFillerPoint(w, d)
  const r = 0.06
  const post: Pt2[] = Array.from({ length: 10 }, (_, i) => {
    const a = (Math.PI * 2 * i) / 10
    return [fx + Math.cos(a) * r, fy + Math.sin(a) * r] as Pt2
  })
  const prism = (poly: Pt2[], z0: number, z1: number) => {
    for (let i = 0; i < poly.length; i++) {
      const j = (i + 1) % poly.length
      tri([poly[i][0], poly[i][1], z0], [poly[j][0], poly[j][1], z0], [poly[j][0], poly[j][1], z1])
      tri([poly[i][0], poly[i][1], z0], [poly[j][0], poly[j][1], z1], [poly[i][0], poly[i][1], z1])
    }
    for (let i = 1; i + 1 < poly.length; i++) {
      tri([poly[0][0], poly[0][1], z1], [poly[i][0], poly[i][1], z1], [poly[i + 1][0], poly[i + 1][1], z1])
      tri([poly[0][0], poly[0][1], z0], [poly[i + 1][0], poly[i + 1][1], z0], [poly[i][0], poly[i][1], z0])
    }
  }
  const box = (x0: number, x1: number, y0: number, y1: number, z0: number, z1: number) =>
    prism([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], z0, z1)
  prism(post.map(([x, y]) => [fx + (x - fx) * 2.2, fy + (y - fy) * 2.2] as Pt2), 0, 0.04)   // floor flange
  prism(post, 0.04, FILLER_H_FT)
  const reach = Math.min(TUB_FILLER_GAP_FT + 0.55, w * 0.25)
  box(fx - 0.03, fx + reach, fy - 0.035, fy + 0.035, FILLER_H_FT - 0.07, FILLER_H_FT)    // spout arm
  box(fx + reach - 0.07, fx + reach, fy - 0.035, fy + 0.035, FILLER_H_FT - 0.3, FILLER_H_FT)   // spout tip
  box(fx - 0.04, fx + 0.04, fy - 0.2, fy - 0.06, FILLER_H_FT - 0.55, FILLER_H_FT - 0.5)   // lever
}

/** the top-view freestanding tub: rim, basin, drain, and the filler beyond the drain end */
export function freestandingTubPlanSymbol(w: number, d: number, filler: boolean): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  const A = w / 2, B = d / 2, cy = d / 2
  const circle = (c: Pt2, r: number): Pt2[] =>
    Array.from({ length: 14 }, (_, i) => {
      const a = (Math.PI * 2 * i) / 14
      return [c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r] as Pt2
    })
  const out: { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] = [
    { pts: oval(A, B, cy), closed: true },
    { pts: oval(A - RIM_T, B - RIM_T, cy), closed: true },
    { pts: circle([-A + 0.75, cy], 0.1), closed: true },
  ]
  if (filler) {
    const [fx, fy] = tubFillerPoint(w, d)
    out.push({ pts: circle([fx, fy], 0.1), closed: true })
    out.push({ pts: [[fx + 0.1, fy], [fx + Math.min(TUB_FILLER_GAP_FT + 0.55, w * 0.25), fy]] })
  }
  return out
}
